import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ODataEntitySetService, ODataServiceFactory } from 'angular-odata';
import { catchError, map, Observable, of } from 'rxjs';
import { IServerSideGetRowsParams } from 'ag-grid-community';
import { LicenseRenewalCandidates, LicenseRenewalWorkflow } from '@workspace-app/odatabackend/QualicoatBackendModel';

export interface CreateRenewalRequest {
  certificationYear: number;
  proposedExpiryDate: Date;
  notes?: string;
}

export interface UpdateSelectionRequest {
  selections: Array<{
    licenseId: string;
    isSelected: boolean;
    notes?: string;
  }>;
  notes?: string;
}

export interface NotifyCertificateAvailabilityRequest {
  specificLicenseIds?: string[];
  contactIds?: string[];
}

export interface ContactInfo {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  organisationName?: string;
  productionSiteName?: string;
  city?: string;
  isCertificateRecipient: boolean;
  relatedLicenseIds: string[];
}

export interface ConfirmedCandidateDto {
  id: string;
  licenseId: string;
  licenseNumber: string;
  coaterName?: string;
  city?: string;
  wasExtended: boolean;
  newExpiryDate?: Date;
  certificateSent: boolean;
  certificateSentOn?: Date;

  // GL Information
  glId?: string;
  glName?: string;
  glAbbreviation?: string;
  glContactName?: string;
  glContactEmail?: string;

  // Production Site Info
  productionSiteName?: string;
  currentExpiryDate?: Date;

  availableContacts: ContactOptionDto[];
  selectedContactIds: string[];
}

export interface ContactOptionDto {
  id: string;
  displayName: string;
  email?: string;
  organisationName?: string;
  productionSiteName?: string;
  isCertificateRecipient: boolean;
  isAutoSelected: boolean;
}

export interface ConfirmedCandidatesDto {
  reviewId: string;
  candidates: ConfirmedCandidateDto[];
}

export interface NotifyCertificateAvailabilityResult {
  emailsSent: number;
  notifications: Array<{
    contactId: string;
    contactName: string;
    email: string;
    success: boolean;
    errorMessage?: string;
  }>;
}

export interface CreateRn1InspectionsResult {
  successCount: number;
  failureCount: number;
  errors: string[];
  createdInspectionIds: string[];
}

export interface CandidateStatus {
  candidateId: string;
  licenseId: string;
  status: string;
  error: string | null;
  attempts: number;
  documentId: string | null;
}

export interface CertificateGenerationStatus {
  totalCandidates: number;
  pendingCount: number;
  processingCount: number;
  completedCount: number;
  failedCount: number;
  queuedCount: number;
  candidates: CandidateStatus[];
}

@Injectable({
  providedIn: 'root',
})
export class LicenseRenewalWorkflowDataService {
  private factory = inject(ODataServiceFactory);
  private httpClient = inject(HttpClient);

  private reviewService: ODataEntitySetService<LicenseRenewalWorkflow>;
  private candidateService: ODataEntitySetService<LicenseRenewalCandidates>;

  constructor() {
    this.reviewService = this.factory.entitySet<LicenseRenewalWorkflow>('LicenseRenewalWorkflows');
    this.candidateService = this.factory.entitySet<LicenseRenewalCandidates>('LicenseRenewalCandidates');
  }

  // Grid data loading for review list
  getReviews(params: IServerSideGetRowsParams) {
    const endRow = params.request.endRow || 20;
    const startRow = params.request.startRow || 0;

    return this.reviewService.entities()
      ?.query((q) => {
        q.expand('Creator,Candidates($expand=License($expand=ProductionSite($expand=Customer)))');
        q.top(endRow);
        q.skip(startRow);
        if (params.request.sortModel?.length > 0) {
          q.orderBy(this.convertToOrderByString(params.request.sortModel));
        }
        if (params.request.filterModel) {
          q.filter(this.convertToFilterString(params.request.filterModel));
        }
      })
      .fetch({ withCount: true })
      .subscribe((response) => {
        const count = response.annots['annotations'].get('@odata.count');
        if (response.entities) {
          return params.success({ rowData: response.entities, rowCount: count });
        }
        return params.fail();
      });
  }

  // Get single review with full details
  getReview(reviewId: string): Observable<LicenseRenewalWorkflow> {
    return this.reviewService
      .entity(reviewId)
      .query((q) => {
        q.expand('Candidates($expand=License($expand=ProductionSite($expand=Customer)))');
      })
      .fetch()
      .pipe(map((response) => response.entity!));
  }

  // Get all reviews ordered by ReviewNumber descending
  getAllReviews(): Observable<LicenseRenewalWorkflow[]> {
    return this.reviewService
      .entities()
      .query((q) => {
        q.orderBy('ReviewNumber desc');
        q.expand('Reviewer,Approver');
      })
      .fetch()
      .pipe(map((response) => response.entities || []));
  }

  // Get active review for a certification year
  getActiveReview(certificationYear: number): Observable<LicenseRenewalWorkflow | null> {
    // Use angular-odata's function support
    const func = this.reviewService.entities().function<{ certificationYear: number }, LicenseRenewalWorkflow>(
      'GetActiveReview'
    );
    return func.callEntity({ certificationYear });
  }

  // Create new review
  createReview(request: CreateRenewalRequest): Observable<unknown> {
    // Normalize to end-of-day UTC to ensure consistent storage regardless of timezone
    const normalizedDate = new Date(Date.UTC(
      request.proposedExpiryDate.getFullYear(),
      request.proposedExpiryDate.getMonth(),
      request.proposedExpiryDate.getDate(),
      23, 59, 59
    ));
    return this.httpClient.post('api/license-renewal-workflows', {
      CertificationYear: request.certificationYear,
      ProposedExpiryDate: normalizedDate.toISOString(),
      Notes: request.notes,
    });
  }

  // Select candidates for review
  selectCandidates(reviewId: string): Observable<unknown> {
    return this.httpClient.post(`api/license-renewal-workflows/${reviewId}/select-candidates`, {});
  }

  // Update candidate selection
  updateSelection(reviewId: string, request: UpdateSelectionRequest): Observable<unknown> {
    return this.httpClient.put(`api/license-renewal-workflows/${reviewId}/selection`, {
      Selections: request.selections.map((s) => ({
        LicenseId: s.licenseId,
        IsSelected: s.isSelected,
        Notes: s.notes,
      })),
      Notes: request.notes,
    });
  }

  // Confirm selection
  confirmSelection(reviewId: string, confirmationNotes?: string): Observable<unknown> {
    return this.httpClient.post(`api/license-renewal-workflows/${reviewId}/confirm-selection`, {
      ConfirmationNotes: confirmationNotes,
    });
  }

  // Get certificate recipients for a review
  getCertificateRecipients(reviewId: string): Observable<ContactInfo[]> {
    return this.httpClient
      .get<{ contacts: ContactInfo[] }>(`api/license-renewal-workflows/${reviewId}/certificate-recipients`)
      .pipe(map((response) => response.contacts || []));
  }

  getCertificateGenerationStatus(reviewId: string): Observable<CertificateGenerationStatus> {
    return this.httpClient.get<CertificateGenerationStatus>(
      `api/license-renewal-workflows/${reviewId}/certificate-generation-status`
    );
  }

  // Get only selected candidates (for GL Notification step onwards)
  getSelectedCandidates(reviewId: string): Observable<LicenseRenewalCandidates[]> {
    return this.candidateService.entities()
      .query((q) => {
        q.filter(`reviewId eq ${reviewId} and isSelected eq true`);
        q.select([
          'id',
          'reviewId',
          'licenseId',
          'wasExtended',
          'originalExpiryDate',
          'newExpiryDate',
          'certificateSent',
          'certificateSentOn',
          'certificateGenerationStatus',
          'certificateDocumentId',
          'glNotificationStatus',
          'glNotificationError',
          'glRecipientEmail',
          'notifiedGlId',
        ]);
        q.expand(
          'License($select=id,completeLicenseNumber;$expand=ProductionSite($expand=Customer($expand=AssignedGl)))'
        );
        q.orderBy('License/ProductionSite/Customer/Name');
        // Note: Removed $select on ProductionSite to allow Customer (Organisation) navigation with derived type AssignedGl
      })
      .fetch()
      .pipe(map((response) => response.entities || []));
  }

  // Get confirmed candidates with contact options
  getConfirmedCandidates(reviewId: string): Observable<ConfirmedCandidatesDto> {
    return this.httpClient.get<ConfirmedCandidatesDto>(
      `api/license-renewal-workflows/${reviewId}/confirmed-candidates`
    );
  }

  // Notify GLs about certificate availability
  notifyCertificateAvailability(reviewId: string, request?: NotifyCertificateAvailabilityRequest): Observable<NotifyCertificateAvailabilityResult> {
    return this.httpClient.post<NotifyCertificateAvailabilityResult>(`api/license-renewal-workflows/${reviewId}/notify-certificate-availability`, {
      SpecificLicenseIds: request?.specificLicenseIds,
      ContactIds: request?.contactIds,
    });
  }

  // Create RN1 inspections for next year
  createRn1Inspections(reviewId: string): Observable<CreateRn1InspectionsResult> {
    return this.httpClient.post<CreateRn1InspectionsResult>(
      `api/license-renewal-workflows/${reviewId}/create-rn1-inspections`,
      {}
    );
  }

  // Skip RN1 inspection creation
  skipRn1Creation(reviewId: string, reason?: string): Observable<{ currentState: string }> {
    return this.httpClient.post<{ currentState: string }>(
      `api/license-renewal-workflows/${reviewId}/skip-rn1-creation`,
      { Reason: reason }
    );
  }

  // Complete the review
  completeReview(reviewId: string, completionNotes?: string): Observable<unknown> {
    return this.httpClient.post(`api/license-renewal-workflows/${reviewId}/complete`, {
      CompletionNotes: completionNotes,
    });
  }

  // Cancel the review
  cancelReview(reviewId: string, cancellationReason: string): Observable<unknown> {
    return this.httpClient.post(`api/license-renewal-workflows/${reviewId}/cancel`, {
      CancellationReason: cancellationReason,
    });
  }

  // Refresh inspections for candidates
  refreshCandidatesInspections(
    reviewId: string
  ): Observable<{ candidatesUpdated: number; totalInspections: number; message: string }> {
    return this.httpClient.post<{ candidatesUpdated: number; totalInspections: number; message: string }>(
      `api/license-renewal-workflows/${reviewId}/refresh-inspections`,
      {}
    );
  }

  // Get candidates for a review
  getCandidates(reviewId: string): Observable<LicenseRenewalCandidates[]> {
    return this.candidateService.entities()
      .query((q) => {
        q.filter(`reviewId eq ${reviewId}`); // Use raw filter string for GUID
        q.expand('License($expand=ProductionSite($expand=Customer($expand=AssignedGl)))');
        // Note: Don't use $select with JSON columns (allInspections) - it causes OData translation errors
        // OData will return all fields by default, including the JSON column
        q.orderBy('License/ProductionSite/Customer/Name');
      })
      .fetch()
      .pipe(
        map((response) => response.entities || []),
        catchError((error) => {
          // If 404, just return empty array (no candidates found)
          if (error?.status === 404) {
            return of([]);
          }
          // For other errors, propagate them
          throw error;
        })
      );
  }

  // Grid data loading for candidates
  getCandidatesForGrid(reviewId: string, params: IServerSideGetRowsParams) {
    const endRow = params.request.endRow || 50;
    const startRow = params.request.startRow || 0;

    return this.candidateService.entities()
      ?.query((q) => {
        q.filter(`reviewId eq ${reviewId}`); // Use raw filter string for GUID
        q.expand('License($expand=ProductionSite($expand=Customer))');
        q.top(endRow);
        q.skip(startRow);
        if (params.request.sortModel?.length > 0) {
          q.orderBy(this.convertToOrderByString(params.request.sortModel));
        }
        if (params.request.filterModel) {
          q.filter(this.convertToFilterString(params.request.filterModel));
        }
      })
      .fetch({ withCount: true })
      .subscribe((response) => {
        const count = response.annots['annotations'].get('@odata.count');
        if (response.entities) {
          return params.success({ rowData: response.entities, rowCount: count });
        }
        return params.fail();
      });
  }

  // Manually extend a single license (outside of review process)
  extendLicenseManually(licenseId: string, newExpiryDate: Date, reason: string): Observable<unknown> {
    // This is a REST API endpoint, not OData action, so HttpClient is correct
    return this.httpClient.post(`api/licenses/${licenseId}/extend-manually`, {
      NewExpiryDate: newExpiryDate.toISOString(),
      ExtensionReason: reason,
    });
  }

  // Retry failed certificate generation
  retryFailedCertificateGeneration(reviewId: string, candidateId: string): Observable<unknown> {
    return this.httpClient.post(`api/license-renewal-workflows/${reviewId}/retry-certificate/${candidateId}`, {});
  }

  // Retry failed GL notification
  retryFailedGlNotification(reviewId: string, candidateId: string): Observable<unknown> {
    return this.httpClient.post(`api/license-renewal-workflows/${reviewId}/retry-gl-notification/${candidateId}`, {});
  }

  private convertToOrderByString(sortModel: unknown[]): string {
    return sortModel
      .map((sort) => {
        const sortObj = sort as { colId: string; sort: string };
        return `${sortObj.colId} ${sortObj.sort}`;
      })
      .join(',');
  }

  private convertToFilterString(filterModel: unknown): string {
    // Implement based on your filter requirements
    // This is a simplified version
    const filters: string[] = [];

    const filterObj = filterModel as Record<string, { filterType: string; filter: unknown; type?: string }>;

    Object.keys(filterObj).forEach((key) => {
      const filter = filterObj[key];
      if (filter.filterType === 'text' && filter.filter) {
        filters.push(`contains(${key}, '${filter.filter}')`);
      } else if (filter.filterType === 'number') {
        if (filter.filter !== null && filter.filter !== undefined) {
          filters.push(`${key} ${filter.type || 'eq'} ${filter.filter}`);
        }
      }
    });

    return filters.join(' and ');
  }
}
